import React, {
    useState,
} from 'react';
import {
    Button,
    InputGroup, FormControl,
} from 'react-bootstrap';
import { faSave } from '@fortawesome/free-regular-svg-icons';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";

import {
    categoryChoices,
    countryChoices,
    languageChoices,
    languageCodeToNameMap,
    sources,
} from './memory';
import {
    CountryFlagWithName,
} from './utils'

export const CategorySelector = ({ category, changeCategory}) => (
    <div>
        <span className="m-1">
            Category:
        </span>
        <Button
            active={category === ""}
            onClick={() => changeCategory("")}
            variant="outline-secondary"
            size="sm"
            className="m-1"
        >
            All
        </Button>
        {categoryChoices.map(choice => (
            <Button
                key={choice}
                active={category === choice}
                onClick={() => changeCategory(choice)}
                variant="outline-secondary"
                size="sm"
                className="m-1"
            >
                {choice}
            </Button>
        ))}
    </div>
);

export const CountrySelector = ({ country, changeCountry }) => {
    const [collapse, setCollapse] = useState(true);
    let choices = collapse ? countryChoices.slice(0, 4) : countryChoices;
    const commonProps = {
        variant: 'outline-secondary',
        className: 'm-1',
        size: 'sm',
    };
    return (
        <div>
            <span className="m-1">
                Country:
            </span>
            <Button
                active={country === ""}
                onClick={() => changeCountry("")}
                {...commonProps}
            >
                All
            </Button>
            { country && !['us', 'cn', 'nz', 'au'].includes(country) && collapse &&
                <Button
                    active={true}
                    {...commonProps}
                >
                    <CountryFlagWithName code={country} />
                </Button>
            }
            { choices.map(choice => (
                <Button
                    active={country === choice}
                    onClick={() => changeCountry(choice)}
                    key={choice}
                    {...commonProps}
                >
                    <CountryFlagWithName code={choice} />
                </Button>
            ))}
            <Button
                onClick={() => setCollapse(!collapse)}
                {...commonProps}
                variant="outline-warning"
            >
                { collapse ? "↓" : "↑" }
            </Button>
        </div>
    );
}

export const SourceSelector = ({
    source = [],
    category = '',
    country = '',
    language = '',
    changeSource,
}) => {
    source = source || [];
    const [collapse, setCollapse] = useState(true);
    const commonProps = {
        variant: 'outline-secondary',
        className: 'm-1',
        size: 'sm',
    };
    const choosedSources = sources.filter(item => {
        let match = true;
        if (category && item.category !== category) {
            match = false;
        } else if (country && item.country !== country) {
            match = false;
        } else if (language && item.language !== language) {
            match = false;
        }
        return match;
    })
    let choices = collapse ? choosedSources.slice(0, 4) : choosedSources;
    const toggleSource = (value) => {
        var index = source.indexOf(value);
        if (index === -1) {
            changeSource([value, ...source]);
        } else {
            changeSource(source.filter(i => i !== value));
        }
    }
    return (
        <div>
            <span className="m-1">
                Source:
            </span>
            <Button
                active={source.length === 0}
                onClick={() => changeSource([])}
                {...commonProps}
            >
                All
            </Button>
            {choices.map(choice => (
                <Button
                    key={choice.id}
                    active={source.indexOf(choice.id) > -1}
                    onClick={() => toggleSource(choice.id)}
                    title={choice.description}
                    {...commonProps}
                >
                    {choice.name}
                </Button>
            ))}
            <Button
                onClick={() => setCollapse(!collapse)}
                {...commonProps}
                variant="outline-warning"
            >
                { collapse ? "↓" : "↑" }
            </Button>
        </div>
    );
};

export const SearchForm = ({ search, changeSearch, submit, save }) => (
    <InputGroup className="mb-3 mt-1 pr-5">
        <FormControl
            placeholder="Topic you want to search"
            aria-label="search"
            aria-describedby="basic-addon2"
            value={search}
            onChange={event => changeSearch(event.target.value)}
        />
        <InputGroup.Append>
            <Button variant="outline-secondary" onClick={submit}>
                <FontAwesomeIcon icon={faSearch} />
                {' '}
                Search
            </Button>
            <Button variant="outline-secondary" onClick={save}>
                <FontAwesomeIcon icon={faSave} />
                {' '}
                Save
            </Button>
        </InputGroup.Append>
    </InputGroup>
)

export const SearchTitleForm = ({
    search, changeSearch,
    searchInTitle, changeSearchInTitle,
    submit, save,
}) => (
    <InputGroup className="mb-3 mt-1 pr-5">
        <FormControl
            placeholder="Keyword you want to search"
            value={search}
            onChange={event => changeSearch(event.target.value)}
        />
        <FormControl
            placeholder="Keyword you want to search in title"
            value={searchInTitle}
            onChange={event => changeSearchInTitle(event.target.value)}
        />
        <InputGroup.Append>
            <Button variant="outline-secondary" onClick={submit}>
                <FontAwesomeIcon icon={faSearch} />
                {' '}
                Search
            </Button>
            <Button variant="outline-secondary" onClick={save}>
                <FontAwesomeIcon icon={faSave} />
                {' '}
                Save
            </Button>
        </InputGroup.Append>
    </InputGroup>
)

export const LanguageSelector = ({ language, changeLanguage}) => (
    <div>
        <span className="m-1">
            Language:
        </span>
        <Button
            active={language === ""}
            onClick={() => changeLanguage("")}
            variant="outline-secondary"
            size="sm"
            className="m-1"
        >
            All
        </Button>
        {languageChoices.map(choice => (
            <Button
                key={choice}
                active={language === choice}
                onClick={() => changeLanguage(choice)}
                variant="outline-secondary"
                size="sm"
                className="m-1"
            >
                {languageCodeToNameMap[choice]}
            </Button>
        ))}
    </div>
);

export const SortSelector = ({ sort, changeSort}) => (
    <div>
        <span className="m-1">
            Sort:
        </span>
        <Button
            active={sort === "publishedAt"}
            onClick={() => changeSort("publishedAt")}
            variant="outline-secondary"
            size="sm"
            className="m-1"
        >
            Published at
        </Button>
        <Button
            active={sort === "relevency"}
            onClick={() => changeSort("relevency")}
            variant="outline-secondary"
            size="sm"
            className="m-1"
        >
            Relevency
        </Button>
        <Button
            active={sort === "popularity"}
            onClick={() => changeSort("popularity")}
            variant="outline-secondary"
            size="sm"
            className="m-1"
        >
            Popularity
        </Button>
    </div>
);

export const TimeSelector = ({ from, changeFrom, to, changeTo }) => (
    <InputGroup className="mb-3 mt-1 pr-5">
        <InputGroup.Prepend>
            <InputGroup.Text>
                Time:
            </InputGroup.Text>
        </InputGroup.Prepend>
        <DatePicker
            dateFormat="yyyy-MM-dd"
            placeholderText="From date"
            className="form-control"
            popperPlacement="bottom-start"
            selected={from && new Date(from)}
            onChange={date => changeFrom(date)}
            popperModifiers={{
                offset: {
                    enabled: true,
                    offset: "5px, 10px"
                },
                preventOverflow: {
                    enabled: true,
                    escapeWithReference: false,
                    boundariesElement: "viewport"
                }
            }}
            isClearable
        />
        <DatePicker
            dateFormat="yyyy-MM-dd"
            placeholderText="To date"
            className="form-control"
            popperPlacement="bottom-start"
            selected={to && new Date(to)}
            onChange={date => changeTo(date)}
            popperModifiers={{
                offset: {
                    enabled: true,
                    offset: "5px, 10px"
                },
                preventOverflow: {
                    enabled: true,
                    escapeWithReference: false,
                    boundariesElement: "viewport"
                }
            }}
            isClearable
        />
    </InputGroup>
);
